/* eslint-disable max-lines */
import * as React from "react";
import { useSelector, useDispatch } from "@teamprevent/admin-redux";

import { Header, Segment, Loader } from "semantic-ui-react";
import { createValidator } from "not-valid";
import * as moment from "moment";

import { AccordionQuestionnaireSection } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireSection";
import { QuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import {
    hrEmailAddressMasterConfig,
    managerEmailAddressMasterConfig,
    masterConfig
} from "@teamprevent/common/questionnaires/questionnaireConfig/physiotherapyAssessmentConsultation/masterConfig";
import * as questionIds from "@teamprevent/common/questionnaires/questionnaireConfig/physiotherapyAssessmentConsultation/questionIds";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import { MultiCheckboxQuestion, MultiCheckbox } from "@teamprevent/common/questionnaires/components/questions/MultiCheckboxQuestion";
import { NumberQuestion } from "@teamprevent/common/questionnaires/components/questions/NumberQuestion";
import { MultiButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { QuestionAnswer } from "@teamprevent/common/questionnaires/model";
import { PhysiotherapyAssessmentConsultationMode } from "@teamprevent/common/physiotherapy/models";
import { RadioButtonQuestion, RadioButton } from "@teamprevent/common/questionnaires/components/questions/RadioButtonQuestion";
import { useQuestionnaireContainer } from "@teamprevent/common/questionnaires";

import { physioMedicalClassificationOptions, medicalClassificationSubCategoryOptions } from "../../../clinicalNotes/models";
import { ClinicalNotesProps } from "../../../clinicalNotes/components/ClinicalNotes";
import { selectors as customerSelectors } from "../../../crud/customer";
import { selectors as ohCaseSelectors } from "../../../crud/ohCase";
import { selectors as taskSelectors } from "../../../task";
import {
    PhysiotherapyAssessmentConsultationTemplate,
    physiotherapyAssessmentConsultationTemplateOptions,
    adherenceToHomeExerciseAndAdvicePlanOptions,
    injuredQuestionOptions,
    smokingStatusQuestionOptions,
    outcomeQuestionOptions,
    currentWorkStatusOptions,
    CurrentWorkStatus,
    ConsentToReleaseTheReportAnswers
} from "../../models";
import { ApplicationState } from "../../../applicationState";
import { loadConsultationDetails } from "../../../clinicalNotes/actions";
import { physiotherapyReferralApi } from "../../physiotherapyReferralApi";

type AllProps = ClinicalNotesProps;

export const PhysiotherapyAssessmentClinicalNotesSectionsV2: React.FunctionComponent<AllProps> = (props: AllProps) => {
    const confirmTemplateToUseSectionId = "ConfirmTemplateToUse";

    const dispatch = useDispatch();

    const customerIdFromState = useSelector(customerSelectors.routeIdSelector);
    const ohCaseIdFromState = useSelector(ohCaseSelectors.routeIdSelector);
    const taskIdFromState = useSelector(taskSelectors.taskIdSelector);
    const currentUser = useSelector((state: ApplicationState) => state.currentUser);
    const consultationAndReferralDetails = useSelector((state: ApplicationState) =>
        state.consultationDetails.find(r => r.consultationTaskId === taskIdFromState));

    const { updateQuestion, sections, readonly } = useQuestionnaireContainer();
    const managerEmailAddressQuestion = sections.find(s => s.id === "ConsentToReleaseTheReport")?.questions[managerEmailAddressMasterConfig.id];
    const hrEmailAddressQuestion = sections.find(s => s.id === "ConsentToReleaseTheReport")?.questions[hrEmailAddressMasterConfig.id];
    const emailQuestionsNeedPopulating = managerEmailAddressQuestion === undefined || hrEmailAddressQuestion === undefined;
    React.useEffect(() => {
        (() =>
        {
            if (emailQuestionsNeedPopulating === false) { return; }
            if (consultationAndReferralDetails === undefined) {
                if (ohCaseIdFromState && taskIdFromState) {
                    dispatch(loadConsultationDetails({ ohCaseId: ohCaseIdFromState, taskId: taskIdFromState }));
                }
            } else {
                updateQuestion({
                    sectionId: "ConsentToReleaseTheReport",
                    id: managerEmailAddressMasterConfig.id,
                    isValid: true,
                    question: managerEmailAddressMasterConfig.questionText,
                    answer: consultationAndReferralDetails.lineManagersEmails
                });
                updateQuestion({
                    sectionId: "ConsentToReleaseTheReport",
                    id: hrEmailAddressMasterConfig.id,
                    isValid: true,
                    question: hrEmailAddressMasterConfig.questionText,
                    answer: consultationAndReferralDetails.hrEmails
                });
            }
        })();
    }, [consultationAndReferralDetails, dispatch, emailQuestionsNeedPopulating, ohCaseIdFromState, taskIdFromState, updateQuestion]);

    const templateQuestion = sections.find(s => s.id === confirmTemplateToUseSectionId)?.questions[questionIds.ConfirmTemplateToUse];
    const template = templateQuestion?.answer as PhysiotherapyAssessmentConsultationTemplate | undefined;
    React.useEffect(() => {
        (async () => {
            if (customerIdFromState && ohCaseIdFromState && taskIdFromState && currentUser && template === undefined) {
                const initialTemplate = await physiotherapyReferralApi.getTemplate(customerIdFromState, ohCaseIdFromState, taskIdFromState);
                const masterConfigQuestion = masterConfig.find(question => question.id === questionIds.ConfirmTemplateToUse)!;
                updateQuestion({
                    sectionId: confirmTemplateToUseSectionId,
                    id: masterConfigQuestion.id,
                    isValid: true,
                    question: masterConfigQuestion.questionText,
                    answer: initialTemplate
                });
            }
        })();
    }, [customerIdFromState, ohCaseIdFromState, taskIdFromState, currentUser, updateQuestion, template]);

    const shouldRenderForCurrentFormMode = (questionId: string) => {
        return masterConfig.find(question => question.id === questionId)?.consultationModes.includes(formMode);
    };

    const formMode = React.useMemo(() => {
        switch (template) {
            case PhysiotherapyAssessmentConsultationTemplate.Face2Face:
                return PhysiotherapyAssessmentConsultationMode.Face2Face;
            case PhysiotherapyAssessmentConsultationTemplate.Telephone:
            case PhysiotherapyAssessmentConsultationTemplate.Video:
                return PhysiotherapyAssessmentConsultationMode.Telephone;
            case PhysiotherapyAssessmentConsultationTemplate.Review:
                return PhysiotherapyAssessmentConsultationMode.Review;
            default:
                return PhysiotherapyAssessmentConsultationMode.Unknown;
        }
    }, [template]);

    const getQuestionConfig = (questionId: string): QuestionConfig => {
        return masterConfig.find(c => c.id === questionId) || { id: questionId, disabled: true };
    };

    const getAnswerToQuestionWithId = (id: string): QuestionAnswer => {
        return props.questions?.find(question => question.id === id)?.answer;
    };

    const isDateInThePast = (value: moment.Moment) => {
        return value?.isSameOrBefore(moment().startOf("day"));
    };

    const isInTheFuture = (value: moment.Moment) => {
        return value?.isSameOrAfter(moment().startOf("day"));
    };

    const scopeOfReportAnswer = getAnswerToQuestionWithId(questionIds.ScopeOfReport);

    if (!template || emailQuestionsNeedPopulating) {
        return <Loader active />;
    }

    return (
        <>
            <AccordionQuestionnaireSection
                title="Confirm Template to Use"
                id="ConfirmTemplateToUse"
                index={4}
            >
                {/* Confirm Template to Use */}
                <DropdownQuestion
                    inline
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionIds.ConfirmTemplateToUse)}
                    options={physiotherapyAssessmentConsultationTemplateOptions}
                    mandatory
                />
            </AccordionQuestionnaireSection>
            <AccordionQuestionnaireSection
                title="Subjective Assessment"
                id="SubjectiveAssessment"
                index={5}
            >
                {/* Subjective Assessment 1 */}
                {shouldRenderForCurrentFormMode(questionIds.ClientsExpectations) &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.ClientsExpectations)}
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.MainProblemOrProgressUpdate) &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.MainProblemOrProgressUpdate)}
                        multiline
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.AdherenceToHomeExerciseAndAdvicePlan) &&
                    <DropdownQuestion
                        inline
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.AdherenceToHomeExerciseAndAdvicePlan)}
                        options={adherenceToHomeExerciseAndAdvicePlanOptions}
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.OnsetDate) &&
                    <OldDateQuestion
                        inline
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.OnsetDate)}
                        validators={[createValidator((value: moment.Moment) => isDateInThePast(value), "Should be today or in the past")]}
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.FirstEpisodeOfPain) &&
                    <MultiButtonQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.FirstEpisodeOfPain)}
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.ChronicProblem) &&
                    <MultiButtonQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.ChronicProblem)}
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.HistoryOfPresentComplaint) &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.HistoryOfPresentComplaint)}
                        multiline
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.Injured) &&
                    <MultiCheckboxQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.Injured)}
                        mandatory
                    >
                        {injuredQuestionOptions.map(opt =>
                            <MultiCheckbox label={opt.label} value={opt.value} key={opt.value} />)}
                    </MultiCheckboxQuestion>
                }
                {shouldRenderForCurrentFormMode(questionIds.PainNRSAverage) &&
                    <NumberQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.PainNRSAverage)}
                        min={0}
                        max={10}
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.PainNRSWorse) &&
                    <NumberQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.PainNRSWorse)}
                        min={0}
                        max={10}
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.PainNRSBest) &&
                    <NumberQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.PainNRSBest)}
                        min={0}
                        max={10}
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.TwentyFourHourPattern) &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.TwentyFourHourPattern)}
                        multiline
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.AggravatingEasingFactors) &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.AggravatingEasingFactors)}
                        multiline
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.Investigations) &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.Investigations)}
                        multiline
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.PreviousTreatment) &&
                    <>
                        <MultiButtonQuestion
                            readonly={readonly}
                            questionConfig={getQuestionConfig(questionIds.PreviousTreatment)}
                            mandatory
                        />
                        {getAnswerToQuestionWithId(questionIds.PreviousTreatment) === MultiButtonQuestion.YesOption &&
                            <>
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.DidThePreviousTreatmentHelp)}
                                    mandatory
                                />
                                <TextQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.DetailsOfPreviousTreatment)}
                                    multiline
                                />
                            </>
                        }
                    </>
                }
                {/* Subjective Assessment 2: CSSQ === Complete Cervical Spine Special Question */}
                {shouldRenderForCurrentFormMode(questionIds.CompleteCSSQs) &&
                    <>
                        <MultiButtonQuestion
                            readonly={readonly}
                            questionConfig={getQuestionConfig(questionIds.CompleteCSSQs)}
                            mandatory
                        />
                        {getAnswerToQuestionWithId(questionIds.CompleteCSSQs) === MultiButtonQuestion.YesOption &&
                            <>
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.CSSQConstantHeadache)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.CSSQNausea)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.CSSQSensoryChanges)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.CSSQDropAttacks)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.CSSQDizziness)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.CSSQSwallowingDifficulties)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.CSSQSpeechDifficulties)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.CSSQVisionDifficulties)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.CSSQAlteredGait)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.CSSQConstantNightPain)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.CSSQUnexplainedWeightLoss)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.CSSQPreviousHistoryOfCancer)}
                                    mandatory
                                />
                            </>
                        }
                    </>
                }
                {/* Subjective Assessment 3: LSSQ === Lumbar Spine Special Question */}
                {shouldRenderForCurrentFormMode(questionIds.CompleteLSSQs) &&
                    <>
                        <MultiButtonQuestion
                            readonly={readonly}
                            questionConfig={getQuestionConfig(questionIds.CompleteLSSQs)}
                            mandatory
                        />
                        {getAnswerToQuestionWithId(questionIds.CompleteLSSQs) === MultiButtonQuestion.YesOption &&
                            <>
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.LSSQBowelOrBladderSymptoms)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.LSSQSaddleAnaesthesia)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.LSSQBilateralLegPainPinsAndNeedles)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.LSSQAlteredGait)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.LSSQConstantNightPain)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.LSSQUnexplainedWeightLoss)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.LSSQPreviousHistoryOfCancer)}
                                    mandatory
                                />
                            </>
                        }
                    </>
                }
                {/* Subjective Assessment 4: PJSQ === Peripheral Joint Special Question */}
                {shouldRenderForCurrentFormMode(questionIds.CompletePJSQs) &&
                    <>
                        <MultiButtonQuestion
                            readonly={readonly}
                            questionConfig={getQuestionConfig(questionIds.CompletePJSQs)}
                            mandatory
                        />
                        {getAnswerToQuestionWithId(questionIds.CompletePJSQs) === MultiButtonQuestion.YesOption &&
                            <>
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.PJSQSevereSwelling)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.PJSQPoppingClicking)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.PJSQGivingWayInstability)}
                                    mandatory
                                />
                                <MultiButtonQuestion
                                    readonly={readonly}
                                    questionConfig={getQuestionConfig(questionIds.PJSQLockingCatching)}
                                    mandatory
                                />
                            </>
                        }
                    </>
                }
                {/* Subjective Assessment 5 */}
                {shouldRenderForCurrentFormMode(questionIds.PastMedicalHistory) &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.PastMedicalHistory)}
                        multiline
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.Medication) &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.Medication)}
                        multiline
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.FamilyHistory) &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.FamilyHistory)}
                        multiline
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.SmokingStatus) &&
                    <DropdownQuestion
                        inline
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.SmokingStatus)}
                        options={smokingStatusQuestionOptions}
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.CommentsOnSmokingStatus) &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.CommentsOnSmokingStatus)}
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.AlcoholConsumptionInUnitsPerWeek) &&
                    <NumberQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.AlcoholConsumptionInUnitsPerWeek)}
                        min={0}
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.SocialHistoryHobbiesPhysicalActivity) &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.SocialHistoryHobbiesPhysicalActivity)}
                        multiline
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.WorkDemands) &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.WorkDemands)}
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.NumberOfDaysOffWorkDueToThisConditionInTheLast12Months) &&
                    <NumberQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.NumberOfDaysOffWorkDueToThisConditionInTheLast12Months)}
                        min={0}
                        max={366}
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.CurrentWorkStatus) &&
                    <>
                        <DropdownQuestion
                            inline
                            readonly={readonly}
                            questionConfig={getQuestionConfig(questionIds.CurrentWorkStatus)}
                            options={currentWorkStatusOptions}
                            mandatory
                        />
                        {getAnswerToQuestionWithId(questionIds.CurrentWorkStatus) === CurrentWorkStatus.NotAtWork &&
                            <OldDateQuestion
                                inline
                                readonly={readonly}
                                questionConfig={getQuestionConfig(questionIds.ExpectedReturnToWorkDate)}
                                validators={[createValidator((value: moment.Moment) => isInTheFuture(value), "Must be in the future")]}
                                mandatory
                            />
                        }
                    </>
                }
                {shouldRenderForCurrentFormMode(questionIds.MedicalClassification) &&
                    <DropdownQuestion
                        inline
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.MedicalClassification)}
                        filterOldOptions
                        options={physioMedicalClassificationOptions}
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.SubCategory) &&
                    <DropdownQuestion
                        inline
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.SubCategory)}
                        filterOldOptions
                        options={medicalClassificationSubCategoryOptions[getAnswerToQuestionWithId(questionIds.MedicalClassification) as string] ?? []}
                        mandatory={medicalClassificationSubCategoryOptions[getAnswerToQuestionWithId(questionIds.MedicalClassification) as string]?.length > 0}
                    />
                }
            </AccordionQuestionnaireSection>
            <AccordionQuestionnaireSection
                title="Objective Assessment"
                id="ObjectiveAssessment"
                index={6}
            >
                {shouldRenderForCurrentFormMode(questionIds.ObjectiveAssessmentFindings) &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.ObjectiveAssessmentFindings)}
                        multiline
                        mandatory
                    />
                }
            </AccordionQuestionnaireSection>
            <AccordionQuestionnaireSection
                title="Assessment Summary"
                id="AssessmentSummary"
                index={7}
            >
                {shouldRenderForCurrentFormMode(questionIds.ClinicalImpression) &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.ClinicalImpression)}
                        mandatory
                    />
                }
                <Header as="h4" textAlign="left">Psycho-social factors</Header>
                {shouldRenderForCurrentFormMode(questionIds.PainMeansHarm) &&
                    <MultiButtonQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.PainMeansHarm)}
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.FearAvoidance) &&
                    <MultiButtonQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.FearAvoidance)}
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.PassiveAttitude) &&
                    <MultiButtonQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.PassiveAttitude)}
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.Catastrophising) &&
                    <MultiButtonQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.Catastrophising)}
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.WorkIsResponsible) &&
                    <MultiButtonQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.WorkIsResponsible)}
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.MentalHealthIssues) &&
                    <MultiButtonQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.MentalHealthIssues)}
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.PsychoSocialFactorsBarriersToWork) &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.PsychoSocialFactorsBarriersToWork)}
                        multiline
                        mandatory
                    />
                }
            </AccordionQuestionnaireSection>
            <AccordionQuestionnaireSection
                title="Management & Outcome"
                id="ManagementAndOutcome"
                index={8}
            >
                {shouldRenderForCurrentFormMode(questionIds.ManagementPlanAndTreatment) &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.ManagementPlanAndTreatment)}
                        multiline
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.WorkRecommendationsAndReturnToWorkPlan) &&
                    <TextQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.WorkRecommendationsAndReturnToWorkPlan)}
                        multiline
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.SelfManagementAdviceAndExercisePlanProvided) &&
                    <MultiButtonQuestion
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.SelfManagementAdviceAndExercisePlanProvided)}
                        mandatory
                    />
                }
                {shouldRenderForCurrentFormMode(questionIds.Outcome) &&
                    <DropdownQuestion
                        inline
                        readonly={readonly}
                        questionConfig={getQuestionConfig(questionIds.Outcome)}
                        options={outcomeQuestionOptions}
                        mandatory
                    />
                }
            </AccordionQuestionnaireSection>
            <AccordionQuestionnaireSection
                title="Consent to release report"
                id="ConsentToReleaseTheReport"
                index={9}
            >
                <p>Please clearly outline the content of your report to the employee and then ask them confirm their preference for consent</p>
                <p><strong>Note:</strong> Once your report has been written it will be managed based on the choice that you make below</p>
                <RadioButtonQuestion
                    hideDivider={!!scopeOfReportAnswer}
                    inline
                    readonly={readonly}
                    questionConfig={getQuestionConfig(questionIds.ScopeOfReport)}
                    mandatory
                >
                    <RadioButton
                        label="I would like to see a copy of my report before it is released to HR and my Manager"
                        value={ConsentToReleaseTheReportAnswers.CopyRequired}
                    />
                    <RadioButton
                        label="I am happy for the report to be sent to me, HR and my Manager at the same time"
                        value={ConsentToReleaseTheReportAnswers.CopyRequiredSameTime}
                    />
                    <RadioButton
                        // eslint-disable-next-line max-len
                        label="Neither of the above (choose this option if any of the email addresses below are wrong or if the employee / manager email address is blank)"
                        value={ConsentToReleaseTheReportAnswers.AlternativeScope}
                    />
                </RadioButtonQuestion>
                {
                    (() => {
                        switch (scopeOfReportAnswer) {
                            case ConsentToReleaseTheReportAnswers.CopyRequired:
                                // eslint-disable-next-line max-len
                                return <Segment>If the employee chooses to see the report first you must notify them that the report will be sent to the Optima Health portal and they will receive an email notification telling them that the report is ready. Advise them that they <strong>must</strong> respond to this within 5 Days or the report will be automatically sent to their Manager and HR. <strong>Check the email address of the manager and employee are correct below. If not, or the employee / manager email address is blank you must select the third option (Neither of the above)</strong></Segment>;
                            case ConsentToReleaseTheReportAnswers.CopyRequiredSameTime:
                                // eslint-disable-next-line max-len
                                return <Segment>Advise the employee that they will receive a copy of the report via the Optima Health portal and that they will receive an email notification telling them the report is ready. <strong>Check the email address of the manager and employee are correct below. If not, or the employee / manager email address is blank you must select the third option (Neither of the above)</strong></Segment>;
                            case ConsentToReleaseTheReportAnswers.AlternativeScope:
                                return (<TextQuestion
                                    inline
                                    mandatory
                                    readonly={false}
                                    questionConfig={getQuestionConfig(questionIds.AlternativeConsentChoice)}
                                    multiline
                                />);
                            default:
                                return null;
                        }
                    })()
                }
                <TextQuestion
                    inline
                    readonly
                    questionConfig={getQuestionConfig(questionIds.EmployeeEmailAddress)}
                    answer={props.employeeRecord?.personalEmailAddress}
                />
                <TextQuestion
                    inline
                    readonly
                    questionConfig={managerEmailAddressMasterConfig}
                />
                <TextQuestion
                    inline
                    readonly
                    questionConfig={hrEmailAddressMasterConfig}
                />
            </AccordionQuestionnaireSection>
        </>
    );
};
